import { DetailedSparkType } from './spark.core';

/**
 * Legacy labels (used when SPARK_LANGUAGE feature flag is disabled).
 * These match the original hardcoded labels used throughout the codebase.
 */
const LEGACY_DETAILED_SPARK_LABELS: Record<DetailedSparkType, string> = {
  leaderboard: 'Single Leaderboard',
  leaderboardMulti: 'Multi-Leaderboard',
  leaderboardLocation: 'Location Leaderboard',
  leaderboardMarket: 'Market-wide Leaderboard',
  goal: 'Goal',
  goalTeam: 'Team Goal',
  goalManager: 'Manager Goal',
  commissionFlat: 'Flat Commission',
  commissionPercentage: 'Percentage Commission',
};

/**
 * New centralized labels (used when SPARK_LANGUAGE feature flag is enabled).
 * This is the single source of truth for spark type language across UI and API.
 */
export const DETAILED_SPARK_LABELS: Record<DetailedSparkType, string> = {
  leaderboard: 'Retailer-wide Leaderboard',
  leaderboardMulti: 'Per-Store Leaderboard',
  leaderboardLocation: 'Store-vs-Store Leaderboard',
  leaderboardMarket: 'Market-wide Leaderboard',
  goal: 'Individual Goal',
  goalTeam: 'Team Goal',
  goalManager: 'Manager Goal',
  commissionFlat: 'Flat Commission',
  commissionPercentage: 'Percentage Commission',
};

/**
 * Gets the display label for a DetailedSparkType.
 * Uses legacy labels if useLegacy is true, otherwise uses new centralized labels.
 *
 * @param sparkType - The detailed spark type
 * @param useLegacy - Whether to use legacy labels (default: false)
 * @returns The human-readable display label
 *
 * @example
 * getDetailedSparkLabel('leaderboard') // 'Retailer-wide Leaderboard'
 * getDetailedSparkLabel('leaderboard', true) // 'Single Leaderboard'
 * getDetailedSparkLabel('goal') // 'Individual Goal'
 */
export function getDetailedSparkLabel(
  sparkType: DetailedSparkType,
  useLegacy: boolean = false,
): string {
  const labels = useLegacy ? LEGACY_DETAILED_SPARK_LABELS : DETAILED_SPARK_LABELS;
  return labels[sparkType] ?? '';
}

/**
 * Gets the display label for a DetailedSparkType with " Spark" suffix.
 * Uses legacy labels if useLegacy is true, otherwise uses new centralized labels.
 *
 * @param sparkType - The detailed spark type
 * @param useLegacy - Whether to use legacy labels (default: false)
 * @returns The display label with " Spark" appended
 *
 * @example
 * getDetailedSparkLabelWithSpark('goal') // 'Individual Goal Spark'
 * getDetailedSparkLabelWithSpark('goal', true) // 'Goal Spark'
 */
export function getDetailedSparkLabelWithSpark(
  sparkType: DetailedSparkType,
  useLegacy: boolean = false,
): string {
  const label = getDetailedSparkLabel(sparkType, useLegacy);
  return label ? `${label} Spark` : '';
}

/**
 * Gets the full label map for all DetailedSparkTypes.
 * Useful for dropdowns, selects, or iterating over all types.
 *
 * @param useLegacy - Whether to use legacy labels (default: false)
 * @returns Record mapping each DetailedSparkType to its display label
 *
 * @example
 * const labels = getDetailedSparkLabelMap();
 * // Use in a select: Object.entries(labels).map(([value, label]) => ...)
 */
export function getDetailedSparkLabelMap(
  useLegacy: boolean = false,
): Record<DetailedSparkType, string> {
  const labels = useLegacy ? LEGACY_DETAILED_SPARK_LABELS : DETAILED_SPARK_LABELS;
  return { ...labels };
}
